import React, { useMemo, useState } from "react"
import { Alert, Box, Button, Typography } from "@mui/material"
import { MaterialReactTable } from "material-react-table"
import { saveAs } from "file-saver"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import TerminalApplications from "../../terminals/components/TerminalApplications"
import { assignedApps, installedApps, appSearchText, appStatusLabel } from "../../terminals/components/terminalApplicationData"
import { terminalFields, terminalReportSheets, buildTerminalReportWorkbook } from "./terminalReportData"

const hidden = ["orgId", "groupId", "terminalId", "createdDateTime", "sseConnectionDtl.instanceName", "sseConnectionDtl.ipAddress", "sseConnectionDtl.connCreatedDateTime", "sseConnectionDtl.connUpdatedDateTime"]
export default function TerminalReportTable({ terminalReportData = [], loading }) {
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState("")
  const columns = useMemo(() => [
    ...terminalFields.map(field => ({ id: field.key, accessorFn: field.value, header: field.header, size: field.numeric ? 140 : 190,
      ...(field.key.includes("DateTime") ? { enableSorting: false } : {}),
      Cell: ({ cell, row }) => ["assignedCount", "installedCount"].includes(field.key)
        ? <Button size="small" onClick={() => row.toggleExpanded()} aria-label={`View ${field.header.toLowerCase()} for ${row.original.serialNo}`}>{cell.getValue()}</Button>
        : ["status", "sseConnectionDtl.connStatus"].includes(field.key) ? appStatusLabel(cell.getValue()) : cell.getValue() ?? "—",
    })),
    { id: "applicationDetails", header: "Application details", accessorFn: row => `${appSearchText(assignedApps(row))} ${appSearchText(installedApps(row))}`,
      size: 260, Cell: ({ row }) => <Button size="small" onClick={() => row.toggleExpanded()}>View assigned & installed apps</Button> },
  ], [])
  const exportReport = async (table, excel) => {
    const rows = table.getPrePaginationRowModel().rows.map(row => row.original)
    setExporting(true); setError("")
    try {
      if (excel) {
        const buffer = await buildTerminalReportWorkbook(rows).xlsx.writeBuffer()
        saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), "terminal_report.xlsx")
      } else {
        const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a3" })
        terminalReportSheets(rows).forEach(([name, fields, items], index) => {
          if (index) doc.addPage()
          doc.text(name, 30, 30)
          autoTable(doc, { startY: 45, head: [fields.map(field => field.header)], body: items.map(row => fields.map(field => String(field.value(row) ?? ""))),
            styles: { fontSize: 7, cellPadding: 3, overflow: "linebreak" }, headStyles: { fillColor: [18, 109, 113] } })
        })
        doc.save("terminal_report.pdf")
      }
    } catch { setError("Unable to generate the report. Please try again.") }
    finally { setExporting(false) }
  }
  return <Box>
    {error && <Alert severity="error">{error}</Alert>}
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Expand a terminal to see its applications and connection details. Excel includes Terminals, Assigned applications, and Installed applications sheets. Exports include all filtered rows across pages.</Typography>
    <MaterialReactTable columns={columns} data={terminalReportData || []} state={{ isLoading: loading }} enableColumnPinning
      initialState={{ density: "compact", pagination: { pageSize: 10, pageIndex: 0 }, columnVisibility: Object.fromEntries(hidden.map(key => [key, false])) }}
      muiTableBodyCellProps={{ sx: { overflowWrap: "anywhere" } }}
      renderDetailPanel={({ row }) => <Box sx={{ p: 2 }}>
        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 3 }}>
          {hidden.map(key => { const field = terminalFields.find(item => item.key === key)
            return <Box key={key}><Typography variant="caption" color="text.secondary">{field.header}</Typography>
              <Typography variant="body2">{field.value(row.original) ?? "Not reported"}</Typography></Box>
          })}
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 3 }}>
          <Box><Typography variant="subtitle1" sx={{ mb: 1 }}>Assigned applications</Typography><TerminalApplications applications={assignedApps(row.original)} /></Box>
          <Box><Typography variant="subtitle1" sx={{ mb: 1 }}>Installed applications</Typography><TerminalApplications applications={installedApps(row.original)} installed /></Box>
        </Box>
      </Box>}
      renderTopToolbarCustomActions={({ table }) => <Box sx={{ display: "flex", gap: 1, p: 1 }}>
        <Button variant="contained" color="success" disabled={loading || exporting || !table.getPrePaginationRowModel().rows.length} onClick={() => exportReport(table, true)}>{exporting ? "Exporting…" : "Export Excel"}</Button>
        <Button variant="outlined" disabled={loading || exporting || !table.getPrePaginationRowModel().rows.length} onClick={() => exportReport(table, false)}>PDF</Button>
      </Box>}
      renderEmptyRowsFallback={() => <Box sx={{ p: 4, textAlign: "center" }}>No terminals to display. Choose filters and search.</Box>} />
  </Box>
}
